import { Badge, Dialog, DialogBody, DialogContent, DialogTrigger } from '@civilgrid/ui/components';
import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useState } from 'react';
import { requestData as apiRequestData } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import EmailLink from '~/components2/EmailLink';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import { downloadAndTrackUtilitiesFile } from '../../utilitiesV2/common/trackUtilitiesFileDownload';
import { RequestStatus, requestStatusMap, statusColor } from '../common';
import store, { type RequestableLayer } from './../store';
import Attachments from './Attachments';

interface RequestButtonProps {
  tableType: 'dataRequested' | 'asbuiltRequested' | 'fiberDetailRequested';
  row: RequestableLayer;
}

const RequestStatusButton = ({ tableType, row }: RequestButtonProps) => {
  const [status, setStatus] = useState<RequestableLayer['status']>('request');
  const [isDownloading, setIsDownloading] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setStatus(row.status);
  }, []);

  const downloadFile = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>, file: any) => {
    e.stopPropagation();

    try {
      setIsDownloading(true);
      await downloadAndTrackUtilitiesFile(file, 'projectFile', projectId);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRequestData = async () => {
    try {
      const utilityId = {
        id: row.id,
        sourceTable: row.sourceTable,
      };

      await apiRequestData(projectId, [utilityId], tableType);
      setStatus('pending');

      // find request in appropriate store, set status to pending
      switch (tableType) {
        case 'dataRequested':
        case 'fiberDetailRequested':
          store.dataRequestable.find((request) => request.id === row.id)!.status = 'pending';
          store.dataRequestable.find((request) => request.id === row.id)!.createdAt = new Date(
            Date.now(),
          ).toLocaleDateString();
          break;
        case 'asbuiltRequested':
          store.asbuiltRequestable.find((request) => request.id === row.id)!.status = 'pending';
          store.asbuiltRequestable.find((request) => request.id === row.id)!.createdAt = new Date(
            Date.now(),
          ).toLocaleDateString();
          break;
      }

      capturePosthogEvent(POSTHOG_EVENTS.utilitiesModuleRequested, {
        project_id: projectId,
        request_type: tableType === 'asbuiltRequested' ? 'as-builts' : 'map',
      });

      Notify.info({
        message: 'Your request has been submitted. You will receive an email when the data is available.',
      });

      return;
    } catch (err) {
      Notify.error({
        title: '⚠️',
        message: 'There was an error during the data request. Please contact support.',
      });
      console.error('Error requesting data: ', err);
      return;
    }
  };

  if (status === 'request' || status === 'dismissed') {
    return (
      <Badge color={statusColor[status]} asChild>
        <button
          type="button"
          onClick={handleRequestData}
          className="cursor-pointer"
          data-testid={`request-status-button-${status}`}
        >
          Request
        </button>
      </Badge>
    );
  }

  const renderStatusContent = () => {
    if (status === 'complete') {
      const mainContent = (
        <>
          Your Request has been completed. If you have any questions, please reach out to us at{' '}
          <EmailLink>support@civilgrid.com</EmailLink>.
        </>
      );

      if (row.files.length > 0) {
        if (isDownloading) {
          return (
            <div className="flex max-w-xl flex-col items-center gap-8">
              <div>{mainContent}</div>
              <div className="flex h-full w-full items-center justify-center">
                <SkeletonSpinnerCentered />
              </div>
            </div>
          );
        }

        return (
          <div className="max-w-xl">
            {mainContent}
            <br />
            <br />
            The following files are available for download:
            <br />
            <br />
            {row.files.map((file, i) => {
              const testId = file.id
                ? `utilities-legacy-file-download-${file.id}`
                : `utilities-legacy-file-download-${(file.name || `file-${i + 1}`).replace(/[^a-z0-9-]/gi, '-').toLowerCase()}`;
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: legacy
                // biome-ignore lint/a11y/noStaticElementInteractions: legacy
                // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  key={`${file.name}-${i}`}
                  className="flex cursor-pointer items-center gap-2 rounded px-0.5 py-1 hover:bg-grey-20"
                  onClick={(e) => downloadFile(e, file)}
                  data-testid={testId}
                >
                  <Icon icon="download" />
                  {file.name || `File-${i + 1}`}
                </div>
              );
            })}
          </div>
        );
      }

      return <>{mainContent}</>;
    } else if (status === 'actionRequired') {
      return (
        <div className="flex max-w-xl flex-col space-y-4">
          <div>
            This data request requires your review and input to proceed. Situations that require your action may
            include:
          </div>

          <ul className="list-inside list-disc">
            <li>Review and approve agency pass-through fees.</li>
            <li>Review and sign NDA from agency.</li>
            <li>Additional project info requested by agency.</li>
            <li>Agency certification letter required.</li>
          </ul>

          <div>
            Please check your email inbox for emails from{' '}
            <EmailLink email="dataoutreach@civilgrid.com">dataoutreach@civilgrid.com</EmailLink> with instructions on
            what actions are needed from you. If you did not receive this email, please contact us at{' '}
            <EmailLink>dataoutreach@civilgrid.com</EmailLink> for assistance.
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-xl">
        Your request is currently being processed and you will receive an email when the data is available. If you have
        any questions, please reach out to us at <EmailLink>support@civilgrid.com</EmailLink>.
      </div>
    );
  };

  return (
    <Dialog>
      <div
        className={`flex cursor-pointer items-center justify-start space-x-2 ${
          status === RequestStatus.Cancelled && 'cursor-not-allowed'
        }`}
      >
        <Badge color={statusColor[status]} asChild>
          <DialogTrigger asChild>
            <button
              type="button"
              disabled={status === RequestStatus.Cancelled}
              className="cursor-pointer disabled:cursor-not-allowed"
              data-testid={`request-status-button-${status}`}
            >
              {requestStatusMap[status]}
            </button>
          </DialogTrigger>
        </Badge>
        {status === RequestStatus.Complete && row.files.length > 0 && (
          <DialogTrigger asChild>
            <button
              type="button"
              aria-label="View files"
              className="flex cursor-pointer items-center rounded-sm focus-ring"
              data-testid={`request-status-files-button-${row.id}`}
            >
              <Attachments status={status} row={row} />
            </button>
          </DialogTrigger>
        )}
      </div>
      <DialogContent
        title="Status"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid={`request-status-${row.id}-dialog`}
      >
        <DialogBody>{renderStatusContent()}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default RequestStatusButton;
